import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Layout from "../Layout";
import Loader from "../animations/Loader";

const API_URL = process.env.REACT_APP_HR_API_URL || "";

const AVAILABILITY_OPTIONS = [
  { value: "full-time", label: "Full-time" },
  { value: "part-time", label: "Part-time" },
];

const EDUCATION_OPTIONS = [
  { value: "not_applicable", label: "Not applicable (N/A)" },
  { value: "currently_studying", label: "Currently studying at university/college" },
  { value: "completed_degree", label: "Completed a degree" },
];

// Normalise source from URL: source param, utm_source, or 'direct'
function getSourceFromParams(searchParams) {
  const source = searchParams.get("source") || searchParams.get("utm_source") || "";
  const s = String(source).trim().toLowerCase();
  if (!s) return "direct";
  const allowed = ["linkedin", "facebook", "instagram", "qr_code", "indeed", "direct", "website", "public-job-board"];
  if (allowed.includes(s)) return s;
  return s || "direct";
}

export default function Apply() {
  const [searchParams] = useSearchParams();
  const jobRef = searchParams.get("job") || searchParams.get("jobPostingId") || "";
  const [job, setJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitState, setSubmitState] = useState("idle"); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState("");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    availability: "",
    addressLine1: "",
    city: "",
    postcode: "",
    education: "",
    whyWorkForUs: "",
    notes: "",
  });
  const [cvFile, setCvFile] = useState(null);

  const source = getSourceFromParams(searchParams);
  // If no source/utm_source was provided on the /apply entry page, keep legacy behaviour
  // (drill-down links default to `public-job-board`).
  const hasSourceParam = Boolean((searchParams.get("source") || searchParams.get("utm_source") || "").trim());

  useEffect(() => {
    if (!jobRef.trim()) {
      setLoading(true);
      setNotFound(false);
      setJob(null);
      axios
        .get(`${API_URL}/ats/job-postings/public`)
        .then((res) => {
          setJobs(Array.isArray(res.data) ? res.data : []);
        })
        .catch(() => {
          setJobs([]);
        })
        .finally(() => setLoading(false));
      return;
    }
    setLoading(true);
    setNotFound(false);
    setJobs([]);
    axios
      .get(`${API_URL}/ats/job-postings/public/${encodeURIComponent(jobRef.trim())}`)
      .then((res) => {
        setJob(res.data);
      })
      .catch((err) => {
        if (err.response?.status === 404) setNotFound(true);
        else setErrorMessage("Could not load job details. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [jobRef]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!job || !form.email?.trim()) return;
    if (!cvFile || !(cvFile instanceof File)) {
      setErrorMessage("CV / Resume is required.");
      return;
    }
    setSubmitState("submitting");
    setErrorMessage("");

    const data = new FormData();
    data.append("job", job.jobPostingId.toString());
    data.append("firstName", (form.firstName || "").trim());
    data.append("lastName", (form.lastName || "").trim());
    data.append("email", (form.email || "").trim());
    data.append("phone", (form.phone || "").trim());
    if ((form.availability || "").trim()) data.append("availability", (form.availability || "").trim());
    if ((form.addressLine1 || "").trim()) data.append("addressLine1", (form.addressLine1 || "").trim());
    if ((form.city || "").trim()) data.append("city", (form.city || "").trim());
    if ((form.postcode || "").trim()) data.append("postcode", (form.postcode || "").trim());
    if ((form.education || "").trim()) data.append("education", (form.education || "").trim());
    if ((form.whyWorkForUs || "").trim()) data.append("whyWorkForUs", (form.whyWorkForUs || "").trim());
    data.append("notes", (form.notes || "").trim());
    data.append("source", source);
    data.append("cv", cvFile);

    axios
      .post(`${API_URL}/ats/apply`, data)
      .then(() => setSubmitState("success"))
      .catch((err) => {
        setSubmitState("error");
        const msg = err.response?.data?.error || err.response?.data?.message || "Something went wrong. Please try again.";
        setErrorMessage(msg);
      });
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 8 }, textAlign: "center" }}>
          <Stack spacing={2} alignItems="center">
            <Loader />
            <Typography variant="body1">Loading…</Typography>
          </Stack>
        </Container>
      </Layout>
    );
  }

  if (!jobRef.trim()) {
    if (jobs.length === 0) {
      return (
        <Layout>
          <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5 } }}>
            <Paper sx={{ p: { xs: 2, sm: 4 } }}>
              <Typography variant="h5" sx={{ mb: 1 }}>Open positions</Typography>
              <Typography variant="body1" color="text.secondary">
                There are no open positions at the moment. Please check back later.
              </Typography>
            </Paper>
          </Container>
        </Layout>
      );
    }
    return (
      <Layout>
        <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5 } }}>
          <Paper sx={{ p: { xs: 2, sm: 4 } }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>Open positions</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose a role to apply.
            </Typography>
            <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {jobs.map((j) => {
                const jobParam = j.slug || j.jobPostingId;
                const applySource = hasSourceParam ? source : "public-job-board";
                const applyUrl = `/apply?job=${encodeURIComponent(jobParam)}&source=${encodeURIComponent(applySource)}`;
                const title = j.title || j.roleName || "Position";
                return (
                  <ListItemButton
                    key={j.jobPostingId}
                    component={Link}
                    to={applyUrl}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <ListItemText
                      primary={title}
                      secondary={[j.companyName, j.siteName, j.address].filter(Boolean).join(" · ")}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </Container>
      </Layout>
    );
  }

  if (notFound) {
    return (
      <Layout>
        <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5 } }}>
          <Paper sx={{ p: { xs: 2, sm: 4 } }}>
            <Typography variant="h5" sx={{ mb: 1 }}>Job not found</Typography>
            <Typography variant="body1" color="text.secondary">
              This job link is invalid or the position is no longer available. Please check the link or contact the company.
            </Typography>
          </Paper>
        </Container>
      </Layout>
    );
  }

  if (submitState === "success") {
    return (
      <Layout>
        <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5 } }}>
          <Paper sx={{ p: { xs: 2, sm: 4 }, textAlign: "center" }}>
            <Typography variant="h5" sx={{ mb: 1 }}>Application received</Typography>
            <Typography variant="body1" color="text.secondary">
              Thank you for applying for {job?.roleName} at {job?.companyName}. We will review your application and be in touch.
            </Typography>
          </Paper>
        </Container>
      </Layout>
    );
  }

  const jobTitle = job?.title || job?.roleName || "this role";

  return (
    <Layout>
      <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5 } }}>
        <Paper sx={{ p: { xs: 2, sm: 4 } }}>
          <Typography variant="h5" sx={{ mb: 0.5 }}>Apply for {jobTitle}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {job?.companyName}
            {job?.siteName ? ` · ${job.siteName}` : ""}
          </Typography>

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
              {errorMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First name"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  inputProps={{ maxLength: 255 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last name"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  inputProps={{ maxLength: 255 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  type="email"
                  label="Email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  inputProps={{ maxLength: 255 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  inputProps={{ maxLength: 50, inputMode: "tel" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="apply-availability-label">Availability</InputLabel>
                  <Select
                    labelId="apply-availability-label"
                    label="Availability"
                    name="availability"
                    value={form.availability}
                    onChange={handleChange}
                  >
                    <MenuItem value="">Select an option</MenuItem>
                    {AVAILABILITY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="apply-education-label">Education / Degree</InputLabel>
                  <Select
                    labelId="apply-education-label"
                    label="Education / Degree"
                    name="education"
                    value={form.education}
                    onChange={handleChange}
                  >
                    <MenuItem value="">Select an option</MenuItem>
                    {EDUCATION_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Address"
                  name="addressLine1"
                  value={form.addressLine1}
                  onChange={handleChange}
                  inputProps={{ maxLength: 500 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Postcode"
                  name="postcode"
                  value={form.postcode}
                  onChange={handleChange}
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  inputProps={{ maxLength: 255 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  multiline
                  rows={3}
                  label="Why do you want to work for us?"
                  name="whyWorkForUs"
                  value={form.whyWorkForUs}
                  onChange={handleChange}
                  placeholder="Tell us what interests you about this role and our company."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  multiline
                  rows={3}
                  label="Cover note (optional)"
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1} alignItems="flex-start">
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    {cvFile ? "Change CV / Resume" : "Upload CV / Resume (required)"}
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => setCvFile(e.target.files?.[0] || null)}
                    />
                  </Button>
                  {cvFile && (
                    <Typography variant="body2" color="text.secondary">
                      {cvFile.name}
                    </Typography>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitState === "submitting" || !form.email?.trim() || !cvFile}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  {submitState === "submitting" ? <CircularProgress size={24} color="inherit" /> : "Submit application"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Layout>
  );
}
